import { Tabs, Typography } from 'antd'
import useBreakpoint from 'antd/lib/grid/hooks/useBreakpoint'
import { DefaultLayout } from 'components/layouts/DefaultLayout'
import { FollowButton } from 'components/models/owners/FollowButton'
import { MessageButton } from 'components/models/owners/MessageButton/MessageButton'
import {
  ArtworkListTab,
  EventTab,
  TopTab,
} from 'components/pages/owners/OwnerDetail'
import { AccessTab } from 'components/pages/owners/OwnerDetail/AccessTab'
import { LimitContentWidth } from 'components/shared/LimitContentWidth/LimitContentWidth'
import { Loading } from 'components/shared/Loading'
import { SeeMore } from 'components/shared/SeeMore'
import { Sticky, StickyContainer } from 'components/shared/Sticky'
import colors from 'helpers/colors'
import { urls } from 'helpers/urls'
import { useOwner } from 'hooks/owners'
import { useHash } from 'hooks/shared'
import { useRouter } from 'next/router'
import { useTranslation } from 'next-i18next'
import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import styled from 'styled-components'

type Props = {
  ownerFromServerSide?: Owner
}

const getTabContent = (owner: Owner, tabValue: string) => {
  switch (tabValue) {
    case 'top':
      return <TopTab owner={owner} />
    case 'events':
      return <EventTab owner={owner} />
    case 'inventories':
      return <ArtworkListTab owner={owner} />
    case 'access':
      return <AccessTab owner={owner} />
    default:
      return <TopTab owner={owner} />
  }
}

const getBreadCrumbText = (tabValue: string) => {
  switch (tabValue) {
    case 'top':
      return null
    case 'events':
      return 'イベント'
    case 'inventories':
      return '作品'
    case 'access':
      return '所在地'
    default:
      return null
  }
}

/**
 * layoutを構成するのに必要な情報を取得して、OwnerLayoutCoreに渡す
 */
export const OwnerLayout: FC<Props> = ({ ownerFromServerSide }) => {
  const router = useRouter()
  const ownerId = router.query.id
  const { data: owner, isValidating: isValidatingOwner } = useOwner({
    id: typeof ownerId === 'string' ? ownerId : undefined,
    fallbackData: ownerFromServerSide,
  })

  // NOTE: 再取得を行った際にローディングにならないようにownerの有無も確認している
  // TODO: isLoadingが同等の処理となるためswrのバージョンをv2系に上げた際にisLoadingを用いるように変更する
  if (isValidatingOwner && !owner) {
    return (
      <div style={{ marginTop: '20px' }}>
        <Loading />
      </div>
    )
  }

  if (!owner) {
    router.push('/404')
    return null
  }

  return <OwnerLayoutCore owner={owner} />
}

type PropsForCore = {
  owner: Owner
}

const OwnerLayoutCore: FC<PropsForCore> = ({ owner }) => {
  const { xs } = useBreakpoint()
  const { t } = useTranslation()
  const ownerInformationRef = useRef<HTMLDivElement>(null)
  const ownerId = useMemo(() => owner.id, [owner])
  const [isExpandInformation, setIsExpandInformation] = useState<boolean>(false)
  const { hash, setHash } = useHash()

  const { default_address } = owner
  const address = useMemo(() => {
    const parts = [default_address?.city, default_address?.state].filter(
      Boolean,
    )
    if (parts.length > 0) {
      return parts.join(', ')
    }
  }, [default_address?.state, default_address?.city])

  const breadcrumbs = useMemo(() => {
    const breadCrumbText = getBreadCrumbText(hash)
    const breadcrumbs = [
      { text: t('ホーム'), href: urls.top },
      { text: t('ギャラリー'), href: urls.owners },
      {
        text: owner.name,
        href: breadCrumbText ? urls.owner(owner.id) : undefined,
      },
    ]
    if (breadCrumbText) {
      breadcrumbs.push({ text: t(breadCrumbText), href: undefined })
    }
    return breadcrumbs
  }, [hash, t, owner.name, owner.id])

  const descriptionTeaserLength = 200
  const description = useMemo(
    () => (owner?.description ? owner?.description : ''),
    [owner],
  )
  const descriptionExpandable = useMemo(() => {
    if (!description) return false
    return description.length > descriptionTeaserLength
  }, [description])

  const descriptionTeaser = useMemo(() => {
    return descriptionExpandable
      ? `${description.substr(0, descriptionTeaserLength)}${'...'}`
      : description
  }, [descriptionExpandable, description])

  useEffect(() => {
    if (hash === 'inventories') return
    const headerHeight = xs ? 60 : 80
    window.scrollTo({
      top: (ownerInformationRef.current?.offsetTop || 0) - headerHeight,
      behavior: 'smooth',
    })
  }, [hash, xs])

  const handleMovePage = useCallback(
    (tabValue: string) => {
      setHash(tabValue)
    },
    [setHash],
  )

  const renderOwnerTabMenu = useCallback(
    (isSticky: boolean) => (
      <div style={{ position: 'relative', height: '100%' }}>
        <div
          style={{
            position: 'absolute',
            right: '0px',
            top: '-1px',
            width: '53px',
            height: '46px',
            zIndex: 1,
            background:
              'linear-gradient(90deg, rgba(255, 255, 255, 0.00) 0%, #FFF 69.23%)',
          }}
        />
        <StyledTabs
          defaultActiveKey={hash || 'top'}
          xs={Boolean(xs)}
          isSticky={isSticky}
          onChange={handleMovePage}
          animated={false}
        >
          <Tabs.TabPane tab={t('トップ')} key="top" />
          <Tabs.TabPane tab={t('イベント')} key="events" />
          <Tabs.TabPane tab={t('作品')} key="inventories" />
          <Tabs.TabPane tab={t('所在地')} key="access" />
        </StyledTabs>
      </div>
    ),
    [handleMovePage, hash, t, xs],
  )

  return (
    <DefaultLayout onlyLogo={false} fullScreen breadCrumbs={breadcrumbs}>
      <div style={{ paddingBottom: xs ? 80 : 60 }}>
        <div
          style={{
            overflow: 'hidden',
            height: xs ? 320 : 360,
            clipPath: 'inset(0px)',
          }}
        >
          <img
            src={owner.cover_url}
            style={{
              position: 'fixed',
              height: xs ? 320 : 360,
              width: '100%',
              left: '0px',
              objectFit: 'cover',
            }}
          />
        </div>

        <LimitContentWidth>
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'flex-start',
              padding: xs ? 24 : '60px 64px',
              gap: xs ? 24 : 16,
            }}
            ref={ownerInformationRef}
          >
            <div
              style={{
                display: 'flex',
                flexDirection: xs ? 'column' : 'row',
                justifyContent: 'space-between',
                alignSelf: 'stretch',
                gap: 24,
              }}
            >
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'flex-start',
                }}
              >
                <img
                  src={owner.online_store_icon_url}
                  height={xs ? '60px' : '68px'}
                  width={xs ? '60px' : '68px'}
                  style={{ border: '0.5px solid #c4c4c4' }}
                />
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    marginLeft: '12px',
                  }}
                >
                  <Typography.Text
                    style={{ fontSize: '28px', fontWeight: 500, lineHeight: 1 }}
                  >
                    {owner.name}
                  </Typography.Text>
                  <Typography.Text
                    style={{ fontSize: '16px', color: colors.gray4 }}
                  >
                    {address}
                  </Typography.Text>
                </div>
              </div>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gridGap: '8px',
                  width: xs ? '100%' : '',
                  marginLeft: 'auto',
                }}
              >
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'row',
                    justifyContent: 'space-between',
                    width: '100%',
                    gap: 8,
                    height: 36,
                  }}
                >
                  <FollowButton
                    style={{
                      display: 'flex',
                      flex: 1,
                      width: xs ? '' : 147,
                      padding: '12px 16px',
                    }}
                    ownerId={owner.id}
                    size={'md'}
                  />
                  <MessageButton
                    ownerId={ownerId}
                    style={{ width: 147, flex: 1 }}
                  />
                </div>
              </div>
            </div>

            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'flex-start',
              }}
            >
              <Typography.Paragraph
                style={{ whiteSpace: 'pre-line', marginBottom: 4 }}
                ellipsis
              >
                {isExpandInformation ? owner.description : descriptionTeaser}
              </Typography.Paragraph>
              {descriptionExpandable && (
                <SeeMore
                  expanded={isExpandInformation}
                  setExpanded={setIsExpandInformation}
                  blackTheme
                />
              )}
            </div>
          </div>
        </LimitContentWidth>

        <StickyContainer>
          <Sticky>
            {({ style, isSticky }) => {
              return (
                <div>
                  {isSticky ? (
                    /* limit content width and align content center */
                    <div
                      style={{
                        ...style,
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'center',
                        alignItems: 'center',
                        backgroundColor: 'white',
                        zIndex: 2,
                        borderBottom: `1px solid ${colors.grey}`,
                      }}
                    >
                      {/* PaddingTopは40px+16px or 80px:16px */}
                      <div
                        style={{
                          padding: xs ? '56px 0 0 0' : '80px 64px 0 64px',
                          gap: xs ? 24 : 16,
                          zIndex: 1,
                          overflowX: 'scroll',
                          maxWidth: 1280,
                          width: '100%',
                        }}
                      >
                        <div
                          style={{
                            display: 'flex',
                            flexDirection: 'row',
                            alignItems: 'center',
                          }}
                        >
                          {!xs && (
                            <div
                              style={{
                                display: 'flex',
                                flex: 1,
                                alignItems: 'center',
                                justifyContent: 'flex-start',
                                padding: '16px 0px',
                              }}
                            >
                              <img
                                src={owner.online_store_icon_url}
                                height={40}
                                width={40}
                                style={{ border: '0.5px solid #c4c4c4' }}
                              />
                              <div
                                style={{
                                  display: 'flex',
                                  flexDirection: 'column',
                                  marginLeft: '12px',
                                }}
                              >
                                <Typography.Text
                                  style={{
                                    fontSize: '20px',
                                    fontWeight: 500,
                                    lineHeight: 1,
                                  }}
                                >
                                  {owner.name}
                                </Typography.Text>
                              </div>
                            </div>
                          )}
                          <div
                            style={{
                              flex: 1,
                              maxWidth: '600px',
                              height: xs ? 46 : 72,
                            }}
                          >
                            {renderOwnerTabMenu(isSticky)}
                          </div>
                        </div>
                      </div>
                    </div>
                  ) : (
                    renderOwnerTabMenu(isSticky)
                  )}
                </div>
              )
            }}
          </Sticky>
          <LimitContentWidth>
            <div style={{ minHeight: 300 }}>{getTabContent(owner, hash)}</div>
          </LimitContentWidth>
        </StickyContainer>
      </div>
    </DefaultLayout>
  )
}

const StyledTabs = styled(Tabs)<{ xs: boolean; isSticky: boolean }>`
  height: 100%;

  .ant-tabs-nav-list {
    width: 100%;
    height: 100%;
    justify-content: ${(props) => (props.xs ? 'flex-start' : 'center')};
    overflow-x: scroll;
    border-bottom: ${(props) =>
      props.isSticky ? 'none' : `1px solid ${colors.grey}`};
    > .ant-tabs-ink-bar {
      height: 1px;
    }
  }
  .ant-tabs-nav {
    height: 100%;
    margin-bottom: 0px;
    /* 画面幅いっぱいに表示したいがant-tabsが自動でnavの幅のborderを付与するので非表示にする。*/
    ${(props) =>
      props.isSticky &&
      `
      ::before {
        display: none;
      }
    `}
  }
  .ant-tabs-ink-bar {
    /* 上書きできないため!importantをつけている */
    height: 2px !important;
  }
  .ant-tabs-tab-active > .ant-tabs-tab-btn {
    font-weight: 700;
  }
  .ant-tabs-tab {
    justify-content: center;
    margin-left: 0px;
    ${(props) =>
      props.isSticky
        ? `padding: ${props.xs ? '16px' : '16px 16px 32px 16px'};`
        : 'padding: 16px;'}
    /* flex:1とすると表示が正しく行われないので一旦幅をハードコードで対応している */
    ${(props) => !props.xs && 'width: 216px;'} {
  }
`
